/**
 * <countdown-timer> - a small self-contained countdown custom element.
 * Counts down to a fixed end time stored in localStorage per-visitor so the
 * deadline persists across page views instead of resetting on every reload.
 *
 * Usage:
 *   <countdown-timer data-duration-hours="24" data-storage-key="lovy-sale-countdown">
 *     <span ref="hours"></span>:<span ref="minutes"></span>:<span ref="seconds"></span>
 *   </countdown-timer>
 */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    const storageKey = this.dataset.storageKey || 'countdown-timer-deadline';
    const durationHours = Number(this.dataset.durationHours) || 24;

    let deadline = Number(window.localStorage?.getItem(storageKey));
    const now = Date.now();

    if (!deadline || Number.isNaN(deadline) || deadline < now) {
      deadline = now + durationHours * 60 * 60 * 1000;
      try {
        window.localStorage?.setItem(storageKey, String(deadline));
      } catch (error) {
        // localStorage unavailable (private mode, etc.) - timer still runs for this page view.
      }
    }

    this.deadline = deadline;
    this.hoursEl = this.querySelector('[ref="hours"]');
    this.minutesEl = this.querySelector('[ref="minutes"]');
    this.secondsEl = this.querySelector('[ref="seconds"]');

    this.tick();
    this.interval = window.setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    if (this.interval) window.clearInterval(this.interval);
  }

  tick() {
    const remainingMs = this.deadline - Date.now();

    if (remainingMs <= 0) {
      this.dispatchEvent(new CustomEvent('countdown:complete', { bubbles: true }));
      this.setValues(0, 0, 0);
      window.clearInterval(this.interval);
      return;
    }

    const totalSeconds = Math.floor(remainingMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.setValues(hours, minutes, seconds);
  }

  setValues(hours, minutes, seconds) {
    const pad = (value) => String(value).padStart(2, '0');
    if (this.hoursEl) this.hoursEl.textContent = pad(hours);
    if (this.minutesEl) this.minutesEl.textContent = pad(minutes);
    if (this.secondsEl) this.secondsEl.textContent = pad(seconds);
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
